import SupportHandoffButton from './SupportHandoffButton';
import { hasBlockingRepairOrders } from '../utilities/repairOrderRouting';
import SupportNotes from './SupportNotes';
import { canReadCallHistory } from '../pages/CallHistory/callHistoryUtils';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { handleGetRequest } from '../service/GetTemplate';
import { handlePostRequest } from '../service/PostTemplate';
import { handlePatchRequest } from '../service/PatchTemplate';
import { endPoints } from '../Config';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import { formatLocalizedDateTime, formatTimeFromMinutes } from '../utilities/formatTime';
import usePreferredLanguage from '../utilities/usePreferredLanguage';
import { translateUiText, translateUiToken } from '../utilities/siteTranslations';
import PhotoUploader from './PhotoUploader';
import {
    getComplaintAutomationState,
    requiresHumanFollowupConfirmation
} from '../utilities/complaintAutomationUi';

const resolveChatImageUrl = (photoValue) => {
    if (!photoValue || typeof photoValue !== 'string') return null;
    const trimmed = photoValue.trim();
    if (!trimmed) return null;

    // Already absolute (http/https) or embedded (data URI)
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:')) {
        return trimmed;
    }

    const imageBase =
        process.env.REACT_APP_IMAGE_BASE_URL ||
        process.env.REACT_APP_BACKEND_URL?.replace(/\/api\/v1\/?$/, '/') ||
        `${window.location.origin}/`;
    const normalizedBase = imageBase.endsWith('/') ? imageBase : `${imageBase}/`;
    const normalizedPath = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
    return `${normalizedBase}${normalizedPath}`;
};

const getChatMediaType = (message = {}) => {
    const explicitType = String(message.mediaType || '').trim().toLowerCase();
    if (explicitType === 'image' || explicitType === 'video') return explicitType;

    const value = String(message.text || '').trim().toLowerCase();
    if (!value) return message.isImage ? 'image' : 'text';
    if (/\.(mp4|mov|webm|m4v)(\?.*)?$/.test(value) || value.startsWith('videos/')) return 'video';
    if (message.isImage || /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/.test(value) || value.startsWith('images/')) return 'image';
    return 'text';
};

const formatChatTimestamp = (value, language = 'es') => formatLocalizedDateTime(value, language);
const formatChatMessageTimestamp = (value, language = 'es') =>
    formatLocalizedDateTime(value, language, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
const normalizeAccountNumber = (value) => String(value || '').replace(/\D/g, '');
const REFUND_BANK_OPTIONS = ['Banco General', 'BAC', 'Banistmo', 'BANISI', 'Banco Nacional', 'Towerbank', 'Banesco', 'Davivienda', 'Multibank', 'BI Bank', 'Global Bank', 'St Georges Bank', 'Caja de Ahorros', 'Other'];
const REFUND_SUPPORT_USER_ALLOWLIST = new Set([
    'valentina@coffeevending.com.pa',
    'maria.chacon@amevel.com'
]);
const formatAdminUserName = (user) => {
    if (!user) return null;
    if (typeof user === 'string') return user;
    return (
        user.name ||
        `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
        user.email ||
        null
    );
};
const formatRefundAuditText = (audit = {}) => {
    const actionKey = String(audit.action || '').trim().toLowerCase();
    const statusKey = String(audit.status || '').trim().toLowerCase();

    const financeLabels = {
        finance_mark_ready: 'Listo para exportar a TXT',
        finance_start_manual: 'Gestión manual pendiente',
        finance_complete_manual: 'Pago manual registrado',
        finance_reopen_rejected: 'Rechazo corregido: pendiente de revisión',
        finance_request_correction: 'Corrección de datos solicitada'
    };
    if (financeLabels[actionKey]) return `${financeLabels[actionKey]}${audit.note ? `: ${audit.note}` : ''}`;
    if (actionKey.startsWith('manual_payment_notification_')) {
        const labels = { sent: 'Notificación aceptada por el proveedor', failed: 'Notificación fallida: el pago sigue registrado', suppressed: 'Notificación desactivada' };
        return labels[actionKey.replace('manual_payment_notification_', '')] || 'Notificación de pago manual';
    }
    if (statusKey === 'rejected' || actionKey.includes('reject')) {
        return audit.note ? `Refund not completed: ${audit.note}` : 'Refund not completed';
    }
    if (statusKey === 'approved' || actionKey.includes('approved')) {
        return audit.note ? `Refund completed: ${audit.note}` : 'Refund completed';
    }
    if (statusKey === 'customer_notified' || actionKey.includes('notified')) {
        return audit.note ? `Customer notified: ${audit.note}` : 'Customer notified';
    }
    if (statusKey === 'refund_in_process') {
        return audit.note ? `Refund in process: ${audit.note}` : 'Refund in process';
    }

    return null;
};
const isRefundOutcomeAudit = (audit = {}) => {
    const actionKey = String(audit.action || '').trim().toLowerCase();
    const statusKey = String(audit.status || '').trim().toLowerCase();
    return (
        actionKey.startsWith('finance_') || actionKey.startsWith('manual_payment_notification_') ||
        ['approved', 'rejected', 'customer_notified', 'refund_in_process'].includes(statusKey) ||
        actionKey.includes('approved') ||
        actionKey.includes('reject') ||
        actionKey.includes('notified')
    );
};
const getRefundCommentsForDisplay = (refund) => {
    const comments = Array.isArray(refund?.comments) ? refund.comments : [];
    const reason = String(refund?.reason || '').trim();
    const hasReasonInComments = comments.some((comment) => String(comment?.text || '').trim() === reason);
    const auditComments = Array.isArray(refund?.audit)
        ? refund.audit
            .filter(isRefundOutcomeAudit)
            .map((entry) => ({
                text: formatRefundAuditText(entry),
                createdBy: entry.updatedBy || null,
                createdAt: entry.createdAt,
                refundAudit: true
            }))
            .filter((entry) => entry.text)
        : [];

    const displayComments = [...comments];
    if (reason && !hasReasonInComments) {
        displayComments.push({
            text: reason,
            createdBy: null,
            createdAt: null,
            legacyReason: true
        });
    }
    return [...displayComments, ...auditComments];
};
const normalizeTeamText = (value) =>
    String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();

// Internal chat entries are the ones the customer never received — audit notes and menu
// markers. They are stored as from:"system", identical to the bot's real outbound
// messages, so before the `internal` flag existed the panel showed them as if they had
// been sent. Rows written before the flag have no value, so fall back to matching the
// known internal patterns.
const LEGACY_INTERNAL_PATTERNS = [
    /^\[system\]/i,
    /^Complaint opened for /i,
    /^Selected Level [123]:/i
];

const isInternalChatEntry = (entry) => {
    if (!entry) return false;
    if (typeof entry.internal === 'boolean') return entry.internal;
    const text = String(entry.text || '');
    return LEGACY_INTERNAL_PATTERNS.some((pattern) => pattern.test(text));
};

const formatComplaintStatusLabel = (status, language = 'es') => {
    const map = {
        open: 'Open',
        in_progress: 'Finance',
        awaiting_customer: 'Awaiting Customer',
        close: 'Closed',
        forwarded: 'Forwarded',
        pending: 'Pending',
        system_close: 'System Closed'
    };
    return translateUiText(map[status] || status || '-', language);
};

const buildFallbackTimeline = (complaint, logs = [], language = 'es') => {
    const t = (value) => translateUiText(value, language);
    if (!complaint) return [];

    const machineLabel =
        complaint?.machine?.uuid ||
        complaint?.machine?.serialNumber ||
        complaint?.machine?.deviceId ||
        t('Unknown machine');
    const locationLabel = complaint?.machine?.location || t('Unknown location');

    const timeline = [
        {
            id: 'created',
            title: t('Ticket created'),
            description: `${machineLabel} ${t('at')} ${locationLabel}`,
            time: formatChatTimestamp(complaint.createdAt, language)
        }
    ];

    if (complaint?.levelOneQuestion?.text) {
        timeline.push({
            id: 'level-1',
            title: t('Level 1 selected'),
            description: complaint.levelOneQuestion.text,
            time: formatChatTimestamp(complaint.createdAt, language)
        });
    }

    if (complaint?.levelTwoQuestion?.text) {
        timeline.push({
            id: 'level-2',
            title: t('Level 2 selected'),
            description: complaint.levelTwoQuestion.text,
            time: formatChatTimestamp(complaint.createdAt, language)
        });
    }

    if (complaint?.levelThreeQuestion?.text) {
        timeline.push({
            id: 'level-3',
            title: t('Level 3 selected'),
            description: complaint.levelThreeQuestion.text,
            time: formatChatTimestamp(complaint.createdAt, language)
        });
    }

    const meaningfulLogs = Array.isArray(logs)
        ? logs
            .filter((log) => log?.team || (log?.status && log.status !== 'open'))
            .map((log, index) => ({
                id: `log-${index}`,
                title: log?.team ? t('Team assigned') : t('Status updated'),
                description: log?.team
                    ? `${t('Assigned to')} ${log.team}`
                    : formatComplaintStatusLabel(log.status, language),
                time: formatChatTimestamp(log.createdAt, language),
                updatedBy: log?.user && log.user !== '-' ? log.user : null
            }))
        : [];

    return [...timeline, ...meaningfulLogs];
};

const ChatBox = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const preferredLanguage = usePreferredLanguage();
    const t = (value) => translateUiText(value, preferredLanguage);
    const tt = (value) => translateUiToken(value, preferredLanguage);

    const navigationState = location.state || {};
    const complaintFromState = navigationState.complaint || navigationState;
    const returnTo =
        navigationState.returnTo ||
        (complaintFromState?.status === 'close' ? '/close-complaints' : '/open-complaints');
    const complaintId =
        complaintFromState?._id ||
        new URLSearchParams(location.search).get('id');
    const currentAdminUser = JSON.parse(
        localStorage.getItem('adminUser') || localStorage.getItem('userDetails') || '{}'
    )?.data?.adminUser || {};
    const currentRoleUid = currentAdminUser?.role?.uid;
    const canSendToTechnicians = ['admin', 'superAdmin', 'support'].includes(currentRoleUid);
    const currentUserEmail = String(currentAdminUser?.email || '').trim().toLowerCase();
    const supportTeamCandidates = [
        currentAdminUser?.team?.name,
        currentAdminUser?.team?.title,
        currentAdminUser?.teamName,
        currentAdminUser?.teamTitle,
        currentAdminUser?.team,
        currentAdminUser?.teamLabel,
        currentAdminUser?.teamId?.name,
        currentAdminUser?.teamId?.title,
        ...(Array.isArray(currentAdminUser?.teams) ? currentAdminUser.teams.map((team) => team?.name || team?.title || team) : [])
    ];
    const canManageRefundFinanceActions = ['admin', 'superAdmin', 'finance'].includes(currentRoleUid) || (
        currentRoleUid === 'support' &&
        (
            REFUND_SUPPORT_USER_ALLOWLIST.has(currentUserEmail) ||
            supportTeamCandidates
                .map(normalizeTeamText)
                .filter(Boolean)
                .some((name) =>
                    name === 'customer support' ||
                    name === 'soporte al cliente' ||
                    name === 'operations' ||
                    name === 'operaciones' ||
                    name.includes('customer support') ||
                    name.includes('soporte al cliente') ||
                    name.includes('operations') ||
                    name.includes('operaciones')
                )
        )
    );
    const [complaint, setComplaint] = useState(null);
    const [logs, setLogs] = useState([]);
    const [repairOrders, setRepairOrders] = useState([]);
    const [technicians, setTechnicians] = useState([]);
    const [selectedTechnician, setSelectedTechnician] = useState(null);
    const [teams, setTeams] = useState([]);
    const [sparePartUsage, setSparePartUsage] = useState([]);
    const [message, setMessage] = useState('');
    const [chatPhoto, setChatPhoto] = useState('');
    const [sendingChatReply, setSendingChatReply] = useState(false);
    const [localMessages, setLocalMessages] = useState([]);
    // AI reply suggestion. Deliberately NOT loaded straight into the composer: a
    // pre-filled box invites an accidental Enter, which would send unreviewed text to a
    // real customer. The agent must click "Usar borrador" first.
    const [aiDraft, setAiDraft] = useState(null);
    const [aiDraftSkipped, setAiDraftSkipped] = useState(null);
    const [aiDraftLoading, setAiDraftLoading] = useState(false);
    const [customerName, setCustomerName] = useState('');
    const [savingCustomerName, setSavingCustomerName] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [imagePreviewVisible, setImagePreviewVisible] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState(null);
    const [creatingRepairOrder, setCreatingRepairOrder] = useState(false);
    const [isTechnicianTeam, setIsTechnicianTeam] = useState(false);
    const [technicalHandoffVisible, setTechnicalHandoffVisible] = useState(false);
    const [technicalHandoffReason, setTechnicalHandoffReason] = useState('');
    const [savingTechnicalHandoff, setSavingTechnicalHandoff] = useState(false);
    const [refundForm, setRefundForm] = useState({
        customerName: '',
        accountNumber: '',
        accountType: '',
        bankName: 'Banco General',
        amount: '',
        currency: 'USD',
        reason: '',
        transactionReference: '',
        status: 'draft'
    });
    const [savingRefund, setSavingRefund] = useState(false);
    const [failedMediaUrls, setFailedMediaUrls] = useState(() => new Set());
    const messagesContainerRef = useRef(null);

    const loadComplaint = async () => {
        if (!complaintId) return;
        const response = await handleGetRequest(endPoints.getComplaintById(complaintId), true, false, dispatch);
        if (response?.data) {
            setComplaint(response.data);
            setFailedMediaUrls(new Set());
            setLogs(response.data.logs || []);
            setCustomerName(response.data.customerName || '');
            const refund = response.data.refund || {};
            const followup = response.data.refundFollowupState || {};
            const normalizeBankName = (value) => {
                const raw = String(value || '').trim();
                if (!raw) return 'Banco General';
                const normalized = raw.toLowerCase();
                if (normalized === 'banco general') return 'Banco General';
                if (normalized === 'bac') return 'BAC';
                if (normalized === 'banistmo') return 'Banistmo';
                if (normalized === 'banco nacional') return 'Banco Nacional';
                if (normalized === 'towerbank') return 'Towerbank';
                if (normalized === 'banesco') return 'Banesco';
                if (normalized === 'davivienda') return 'Davivienda';
                if (normalized === 'multibank') return 'Multibank';
                if (normalized === 'bi bank') return 'BI Bank';
                if (normalized === 'global bank') return 'Global Bank';
                if (normalized === 'st georges bank') return 'St Georges Bank';
                if (normalized === 'caja de ahorros') return 'Caja de Ahorros';
                if (normalized === 'banisi') return 'BANISI';
                return 'Other';
            };
            const normalizeAccountType = (value) => {
                const raw = String(value || '').trim().toLowerCase();
                if (raw === 'ahorro') return 'Ahorro';
                if (raw === 'corriente') return 'Corriente';
                return '';
            };

            const autoCustomerName = String(followup.customerFullName || '').trim();
            const autoAccountNumber = normalizeAccountNumber(followup.accountNumber);
            const autoAccountType = normalizeAccountType(followup.accountType);
            const autoBankName = normalizeBankName(followup.bankEntity);
            const autoAmount = followup.claimAmount != null ? String(followup.claimAmount) : '';
            const manualCustomerName = String(response.data.manualIntake?.customerName || '').trim();

            setRefundForm({
                customerName: refund.customerName || autoCustomerName || manualCustomerName || response.data.customerName || '',
                accountNumber: normalizeAccountNumber(refund.accountNumber) || autoAccountNumber || '',
                accountType:
                    (refund.accountType === 'Ahorro' || refund.accountType === 'Corriente'
                        ? refund.accountType
                        : '') || autoAccountType || '',
                bankName:
                    (REFUND_BANK_OPTIONS.includes(refund.bankName)
                        ? refund.bankName
                        : '') || autoBankName || 'Banco General',
                amount: refund.amount != null ? String(refund.amount) : autoAmount,
                currency: 'USD',
                reason: refund.reason || '',
                transactionReference: '',
                status: refund.status || 'draft'
            });
        }
    };

    const loadRepairOrders = async () => {
        if (!complaintId) return;
        try {
        const response = await handleGetRequest(endPoints.getRepairOrdersByComplaint(complaintId), true, false, dispatch);
            // Handle different response structures: response.data.data (wrapped) or response.data (direct array) or response (direct)
            const repairOrdersData = response?.data?.data || response?.data || response || [];
            const ordersArray = Array.isArray(repairOrdersData) ? repairOrdersData : [];
            setRepairOrders(ordersArray);
        } catch (error) {
            console.error('Error loading repair orders:', error);
            setRepairOrders([]);
        }
    };

    const loadTechnicians = async () => {
        const response = await handleGetRequest(endPoints.getallUsers, true, false, dispatch);
        if (response?.data) {
            // Filter users with technician role if available
            const list = response.data.filter((u) => u?.role?.uid === 'technician' || u?.role?.title === 'Technician');
            setTechnicians(list);
        }
    };

    const loadTeams = async () => {
        const response = await handleGetRequest(endPoints.getallteams, true, false, dispatch);
        if (response?.data) {
            setTeams(response.data);
        }
    };

    const loadSparePartUsage = async () => {
        if (!complaintId) return;
        const response = await handleGetRequest(endPoints.getSparePartUsageByComplaint(complaintId), true, false, dispatch);
        if (response?.data) {
            setSparePartUsage(response.data);
        }
    };

    useEffect(() => {
        loadComplaint();
        loadRepairOrders();
        loadTechnicians();
        loadTeams();
        loadSparePartUsage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [complaintId]);

    useEffect(() => {
        if (complaint?.team) {
            const teamId = typeof complaint.team === 'object' ? complaint.team._id : complaint.team;
            // Check if current team is technician team
            checkIfTechnicianTeam(teamId).then(result => setIsTechnicianTeam(result));
        } else {
            setIsTechnicianTeam(false);
        }
    }, [complaint, teams]);

    useEffect(() => {
        if (!complaint) return;

        const loginData = JSON.parse(localStorage.getItem('adminUser') || '{}');
        const userId = loginData?.data?.adminUser?._id;
        const roleUid = loginData?.data?.adminUser?.role?.uid || 'support';

        const url = process.env.REACT_APP_BACKEND_URL?.replace(/\/api\/v1\/?$/, '') || window.location.origin;
        // The same token every REST call sends. The socket previously identified itself
        // with nothing but a plain `id` string, which the server believed — and this
        // connection can send WhatsApp messages to customers.
        //
        // Reads BOTH storage keys with the same fallback as GetTemplate/PostTemplate.
        // ChatBox looks only at 'adminUser'; the REST layer falls back to 'userDetails',
        // so a session stored under the second key would have working REST calls and an
        // unauthenticated socket — which becomes a locked-out agent once enforcing.
        const authData = JSON.parse(
            localStorage.getItem('adminUser') || localStorage.getItem('userDetails') || '{}'
        );
        const accessToken = authData?.data?.token || authData?.data?.accessToken;
        const socket = io(url, {
            auth: { token: accessToken },
            query: {
                id: userId || 'panel',
                role: roleUid
            },
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 20000
        });

        socket.on('connect', () => {
            // console.log('Socket connected from panel', socket.id);
        });

        socket.on('newMessage', (payload) => {
            const data = payload?.data;
            if (!data?.chat) return;
            // only append messages for this complaint and from customer side (WhatsApp)
            if (complaintId && data._id && complaintId !== data._id) return;
            if (data.chat.from === 'panel') return;
            const chat = data.chat;
            const time = formatChatMessageTimestamp(chat.createdAt, preferredLanguage);
            const customerDisplayName = complaint?.customerName || complaint?.whatsAppNumber || t('Customer');
            const isSystemMessage = chat.from === 'system';
            setLocalMessages((prev) => [
                ...prev,
                {
                    id: (prev[prev.length - 1]?.id || 0) + 1,
                    author: isSystemMessage ? t('System') : customerDisplayName,
                    time,
                    text: chat.text,
                    caption: chat.caption || null,
                    isImage: chat.isImage || false,
                    mediaType: chat.mediaType || (chat.isImage ? 'image' : 'text'),
                    direction: isSystemMessage ? 'system' : 'incoming',
                    internal: isInternalChatEntry(chat)
                }
            ]);
        });

        return () => {
            socket.disconnect();
        };
    }, [complaint, complaintId, preferredLanguage]);

    const activeRepairOrder = useMemo(() => (repairOrders && repairOrders.length > 0 ? repairOrders[0] : null), [repairOrders]);

    const chatMessagesFromBackend = useMemo(() => {
        if (!complaint?.chat || !Array.isArray(complaint.chat)) return [];

        const customerDisplayName = complaint.customerName || complaint.whatsAppNumber || t('Customer');

        return complaint.chat
            .slice()
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
            .map((c, idx) => {
                const isSystemMessage = c.from === 'system';
                const panelAuthor = formatAdminUserName(c.createdBy) || t('Support');
                return {
                    id: idx,
                    author: isSystemMessage ? t('System') : c.from === 'panel' ? panelAuthor : customerDisplayName,
                    time: formatChatMessageTimestamp(c.createdAt, preferredLanguage),
                    text: c.text,
                    caption: c.caption || null,
                    isImage: c.isImage || false,
                    mediaType: c.mediaType || (c.isImage ? 'image' : 'text'),
                    direction: isSystemMessage ? 'system' : c.from === 'panel' ? 'outgoing' : 'incoming',
                    internal: isInternalChatEntry(c)
                };
            });
    }, [complaint, preferredLanguage]);

    const combinedMessages = useMemo(() => [...chatMessagesFromBackend, ...localMessages], [chatMessagesFromBackend, localMessages]);
    const fallbackTimeline = useMemo(() => {
        if (combinedMessages.length > 0) return [];
        return buildFallbackTimeline(complaint, logs, preferredLanguage);
    }, [combinedMessages.length, complaint, logs, preferredLanguage]);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        const el = messagesContainerRef.current;
        if (!el) return;
        // allow small delay for DOM to render
        setTimeout(() => {
            try {
                el.scrollTop = el.scrollHeight;
            } catch (e) {}
        }, 20);
    }, [combinedMessages]);

    const handleSaveCustomerName = async () => {
        if (!complaint?._id || !customerName.trim()) return;
        setSavingCustomerName(true);
        const payload = {
            complaintId: complaint._id,
            customerName: customerName.trim()
        };
        const response = await dispatch(handlePatchRequest(payload, endPoints.updateComplaintCustomer, true, true));
        setSavingCustomerName(false);
        if (response?.status === 200 && response?.data?.data) {
            setComplaint(response.data.data);
            setCustomerName(response.data.data.customerName || '');
        }
    };

    const financeRouteStatuses = [
        'manual_pending',
        'rejection_pending',
        'pending_finance_review',
        'ready_for_bank_upload',
        'revision',
        'exported',
        'submitted_to_bank',
        'approved',
        'customer_notified',
        'refund_in_process',
        'rejected'
    ];
    const hasTechnicalRoute = Boolean(
        complaint?.resolutionRoute === 'technical' ||
        complaint?.technicalHandoffs?.some((handoff) => !handoff.returnedAt) ||
        hasBlockingRepairOrders(repairOrders)
    );
    const hasFinanceRoute = Boolean(
        complaint?.resolutionRoute === 'finance' ||
        complaint?.status === 'in_progress' ||
        financeRouteStatuses.includes(complaint?.refund?.status)
    );
    const hasRouteConflict = hasTechnicalRoute && hasFinanceRoute;
    const refundRiskReview = complaint?.refundRiskReview;
    const requiresLifetimeRefundReview = refundRiskReview?.required === true;

    const complaintStatusOptions = [
        { label: t('Open'), value: 'open' },
        { label: t('Awaiting Customer'), value: 'awaiting_customer' },
        { label: t('Finance'), value: 'in_progress', disabled: !canManageRefundFinanceActions || hasTechnicalRoute },
        { label: t('Closed'), value: 'close' }
    ];

    const refundAccountTypeOptions = [
        { label: t('Ahorro'), value: 'Ahorro' },
        { label: t('Corriente'), value: 'Corriente' }
    ];

    const refundBankEntityOptions = [
        { label: t('Banco General'), value: 'Banco General' },
        { label: t('BAC'), value: 'BAC' },
        { label: t('Banistmo'), value: 'Banistmo' },
        { label: t('BANISI'), value: 'BANISI' },
        { label: t('Banco Nacional'), value: 'Banco Nacional' },
        { label: t('Towerbank'), value: 'Towerbank' },
        { label: t('Banesco'), value: 'Banesco' },
        { label: t('Davivienda'), value: 'Davivienda' },
        { label: t('Multibank'), value: 'Multibank' },
        { label: t('BI Bank'), value: 'BI Bank' },
        { label: t('Global Bank'), value: 'Global Bank' },
        { label: t('St Georges Bank'), value: 'St Georges Bank' },
        { label: t('Caja de Ahorros'), value: 'Caja de Ahorros' },
        { label: t('Other'), value: 'Other' }
    ];
    const refundComments = getRefundCommentsForDisplay(complaint?.refund);
    const correctionPendingAt = complaint?.refundFollowupState?.customerCorrectionPendingAt;
    const correctionReviewedAt = complaint?.refundFollowupState?.customerCorrectionReviewedAt;
    const hasUnreviewedAchCorrection = Boolean(
        correctionPendingAt &&
        (!correctionReviewedAt || new Date(correctionReviewedAt).getTime() < new Date(correctionPendingAt).getTime())
    );
    const cashReturn = complaint?.returnFulfillment || {};
    const assignedCashReturnOperator = formatAdminUserName(cashReturn.assignedOperator);
    const cashReturnAssigned = cashReturn.status === 'assigned_to_operator';

    const getCurrentUserId = () => {
        const stored = localStorage.getItem('userDetails');
        if (!stored) return null;
        try {
            const parsed = JSON.parse(stored);
            return parsed?.data?.adminUser?._id || null;
        } catch {
            return null;
        }
    };

    const buildRefundPayload = () => {
        const normalizedAccountNumber = normalizeAccountNumber(refundForm.accountNumber);
        const isBancoGeneral = String(refundForm.bankName || '').trim().toLowerCase() === 'banco general';
        if (isBancoGeneral && normalizedAccountNumber.length !== 13) {
            toast.error(t('Banco General account number must be exactly 13 digits.'));
            return null;
        }
        return {
            customerName: refundForm.customerName,
            accountNumber: normalizedAccountNumber,
            accountType: refundForm.accountType,
            bankName: refundForm.bankName,
            reason: refundForm.reason,
            currency: 'USD',
            transactionReference: null,
            amount: refundForm.amount === '' ? null : Number(refundForm.amount)
        };
    };

    const handleStatusChange = async (newStatus) => {
        if (!complaint?._id || !newStatus || newStatus === complaint.status) return;
        if (newStatus === 'in_progress') {
            const refundPayload = buildRefundPayload();
            if (!refundPayload) return;
            setUpdatingStatus(true);
            setSavingRefund(true);
            const response = await dispatch(handlePatchRequest(
                refundPayload,
                endPoints.sendComplaintRefundToFinance(complaint._id),
                true,
                true
            ));
            setSavingRefund(false);
            setUpdatingStatus(false);
            if (response?.status === 200) await loadComplaint();
            return;
        }
        setUpdatingStatus(true);
        const userId = getCurrentUserId();
        const payload = {
            complaintId: complaint._id,
            status: newStatus
        };
        // Append userId as query parameter
        const url = userId ? `${endPoints.updateComplaintStatus}?userId=${userId}` : endPoints.updateComplaintStatus;
        const response = await dispatch(handlePatchRequest(payload, url, true, true));
        setUpdatingStatus(false);
        if (response?.status === 200) {
            // Reload complaint to ensure all populated fields are present
            await loadComplaint();
        }
    };

    const submitTechnicalHandoff = async () => {
        if (
            !complaint?._id ||
            technicalHandoffReason.trim().length < 3
        ) return;

        setSavingTechnicalHandoff(true);
        try {
            const response = await dispatch(handlePostRequest({
                reason: technicalHandoffReason.trim()
            }, endPoints.sendComplaintToTechnicians(complaint._id), true, true));
            if (response?.status === 200) {
                setTechnicalHandoffVisible(false);
                setTechnicalHandoffReason('');
                await Promise.all([loadComplaint(), loadRepairOrders()]);
            }
        } finally {
            setSavingTechnicalHandoff(false);
        }
    };

    const updateRefundForm = (field, value) => {
        setRefundForm((prev) => ({ ...prev, [field]: value }));
    };

    const saveRefund = async () => {
        if (!complaint?._id) return;
        const payload = buildRefundPayload();
        if (!payload) return;
        setSavingRefund(true);
        const nextStatus = refundForm.status;
        if (['draft', 'pending_finance_review', 'ready_for_bank_upload', 'revision'].includes(nextStatus)) {
            payload.status = nextStatus;
        }
        const response = await dispatch(handlePatchRequest(payload, endPoints.updateComplaintRefund(complaint._id), true, true));
        setSavingRefund(false);
        if (response?.status === 200) {
            await loadComplaint();
        }
    };

    const refundEditorLocked = !canManageRefundFinanceActions;

    const checkIfTechnicianTeam = async (teamId) => {
        if (!teamId) return false;
        try {
            // First check team name - if team name contains "technician" (case-insensitive), it's a technician team
            const selectedTeam = teams.find(t => t._id === teamId || t.id === teamId);
            if (selectedTeam) {
                const teamName = (selectedTeam.name || '').toLowerCase();
                if (teamName.includes('technician') || teamName.includes('tech')) {
                    return true;
                }
            }
            
            // Also check team members' roles
            const response = await handleGetRequest(endPoints.getTeamMembersById(teamId), true, false, dispatch);
            if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
                // Check if ALL team members have technician role (more strict check)
                const allAreTechnicians = response.data.every((member) => {
                    const roleUid = member?.role?.uid || '';
                    const roleTitle = (member?.role?.title || '').toLowerCase();
                    return roleUid === 'technician' || roleTitle === 'technician';
                });
                
                // Or if majority of members are technicians
                const technicianCount = response.data.filter((member) => {
                    const roleUid = member?.role?.uid || '';
                    const roleTitle = (member?.role?.title || '').toLowerCase();
                    return roleUid === 'technician' || roleTitle === 'technician';
                }).length;
                
                // If all members are technicians, or if more than 50% are technicians
                return allAreTechnicians || (technicianCount > 0 && technicianCount / response.data.length >= 0.5);
            }
        } catch (error) {
            console.error('Error checking team members:', error);
        }
        return false;
    };

    const createRepairOrder = async () => {
        if (!complaint?._id) return;
        setCreatingRepairOrder(true);
        try {
            const payload = { complaintId: complaint._id };
            const response = await dispatch(handlePostRequest(payload, endPoints.createRepairOrder, true, true));
            if (response?.status === 200 || response?.status === 201) {
                await loadRepairOrders();
                await loadComplaint();
                return true;
            }
        } catch (error) {
            console.error('Error creating repair order:', error);
        } finally {
            setCreatingRepairOrder(false);
        }
        return false;
    };

    const handleCreateRepairOrderClick = async () => {
        const success = await createRepairOrder();
        if (success) {
            // Optionally show a success message
        }
    };

    // Check if repair order exists and if team is technician team
    const shouldShowCreateRepairOrderButton = useMemo(() => {
        if (!complaint?._id || !isTechnicianTeam) return false;
        // Check if there's any repair order (only one repair order can exist against a complaint)
        const hasRepairOrder = repairOrders && repairOrders.length > 0;
        return !hasRepairOrder;
    }, [complaint, isTechnicianTeam, repairOrders]);

    // Ask the backend for a reply suggestion. The backend decides whether one is
    // appropriate (it refuses on closed tickets, when the agent already replied, and when
    // the bot is the one waiting on the customer) and returns a reason instead.
    const requestAiDraft = async () => {
        if (!complaint?._id || complaint?.status === 'close') return;
        setAiDraftLoading(true);
        setAiDraft(null);
        setAiDraftSkipped(null);
        try {
            const response = await dispatch(handlePostRequest({}, endPoints.generateComplaintDraft(complaint._id), false, false));
            // handlePostRequest returns the full axios response, and the API wraps its
            // payload as { message, data }. So the draft lives at response.data.data.
            const data = response?.data?.data;
            if (data?.draft?.text) setAiDraft(data.draft);
            else if (data?.skipped) setAiDraftSkipped(data.skipped);
        } catch (e) {
            setAiDraftSkipped('no se pudo generar el borrador');
        } finally {
            setAiDraftLoading(false);
        }
    };

    // A draft answers the conversation as it stood when generated. If the customer has
    // written since, it is stale — drop it rather than suggest a reply to an older message.
    const aiDraftIsStale = useMemo(() => {
        if (!aiDraft || !complaint) return false;
        const currentLength = Array.isArray(complaint.chat) ? complaint.chat.length : 0;
        return aiDraft.basedOnChatLength != null && currentLength > aiDraft.basedOnChatLength;
    }, [aiDraft, complaint]);

    const automationState = complaint ? getComplaintAutomationState(complaint) : null;
    const latestCustomerRequest = [...(complaint?.chat || [])]
        .reverse()
        .find((entry) => entry?.from !== 'panel' && entry?.from !== 'system' && !entry?.internal);
    const botAlreadyAnsweredLatestTurn = requiresHumanFollowupConfirmation(
        latestCustomerRequest,
        automationState?.responseClaim
    );

    const appendOutgoingChat = (chat) => {
        if (!chat) return;
        const time = formatChatMessageTimestamp(chat.createdAt, preferredLanguage);
        setLocalMessages((prev) => [
            ...prev,
            {
                id: (prev[prev.length - 1]?.id || chatMessagesFromBackend.length) + 1,
                author: t('You'),
                time,
                text: chat.text,
                caption: chat.caption || null,
                isImage: chat.isImage || false,
                mediaType: chat.mediaType || (chat.isImage ? 'image' : 'text'),
                direction: 'outgoing'
            }
        ]);
    };

    function confirmHumanFollowup() {
        confirmDialog({
            header: t('Send human follow-up?'),
            message: t('The bot already answered the latest customer message. Your message is different and can be sent as an additional human follow-up.'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: t('Send follow-up'),
            rejectLabel: t('Cancel'),
            acceptClassName: 'p-button-warning',
            accept: () => sendHumanReply(true)
        });
    }

    async function sendHumanReply(allowHumanFollowup = false) {
        setSendingChatReply(true);
        try {
            const response = await dispatch(handlePostRequest(
                {
                    message: message.trim(),
                    ...(chatPhoto ? { imageData: chatPhoto } : {}),
                    ...(allowHumanFollowup ? { allowHumanFollowup: true } : {})
                },
                endPoints.replyToComplaint(complaintId),
                false,
                false,
                true
            ));
            const errorCode = response?.data?.data?.code;
            if (errorCode === 'HUMAN_FOLLOWUP_CONFIRMATION_REQUIRED' && !allowHumanFollowup) {
                confirmHumanFollowup();
                return;
            }
            if (response?.status !== 200) {
                const messageByCode = {
                    DUPLICATE_HUMAN_REPLY: 'This exact follow-up was already sent',
                    CUSTOMER_RESPONSE_IN_PROGRESS: 'Another response is still being sent. Please try again.'
                };
                toast.warn(t(messageByCode[errorCode] || response?.data?.message || 'Something went wrong !!'));
                return;
            }
            const chat = response?.data?.data?.chat;
            if (!chat) return;
            appendOutgoingChat(chat);
            setMessage('');
            setChatPhoto('');
            toast.success(t(chatPhoto ? 'Photo sent' : 'Message sent'), { autoClose: 1000 });
            await loadComplaint();
        } finally {
            setSendingChatReply(false);
        }
    }

    const handleSend = async () => {
        if (sendingChatReply) return;
        if (!message.trim() && !chatPhoto) return;
        if (complaint?.status === 'close') return;

        if (botAlreadyAnsweredLatestTurn) {
            confirmHumanFollowup();
            return;
        }
        await sendHumanReply(false);
    };

    const handleAssignTechnician = async () => {
        if (!activeRepairOrder?._id || !selectedTechnician) return;
        const payload = { technicianId: selectedTechnician };
        // Backend route is PATCH /repairOrder/{id}/assignTechnician
        const response = await dispatch(handlePatchRequest(payload, endPoints.assignRepairTechnician(activeRepairOrder._id), true));
        if (response?.status === 200) {
            await loadRepairOrders();
        }
    };

    const technicianOptions = useMemo(
        () =>
            technicians.map((t) => ({
                label: `${t.firstName || ''} ${t.lastName || ''}`.trim(),
                value: t._id
            })),
        [technicians]
    );

    const statusTemplate = (rowData) => {
        const status = rowData.status === 'completed' ? t('Completed') : t('Open');
        const color = rowData.status === 'completed' ? '#16a34a' : '#eab308';
        return <span style={{ fontWeight: 'bold', color }}>{status}</span>;
    };

    const sparePartStatusTemplate = (rowData) => {
        const color = rowData.status === 'completed' ? '#16a34a' : rowData.status === 'pending' ? '#eab308' : '#f97316';
        return <span style={{ fontWeight: 'bold', color }}>{tt(rowData.status)}</span>;
    };

    const getCurrentSupervisorId = () => {
        const stored = localStorage.getItem('userDetails');
        if (!stored) return null;
        try {
            const parsed = JSON.parse(stored);
            return parsed?.data?.adminUser?._id || null;
        } catch {
            return null;
        }
    };

    const handleApproveRejectUsage = async (usage, action) => {
        const usageId = usage?._id;
        const supervisorId = getCurrentSupervisorId();
        if (!supervisorId || !usageId) return;

        const payload = action === 'rejected' ? { usageId, supervisorId, reason: window.prompt('Reason for rejection (optional):', '') || '' } : { usageId, supervisorId };

        const endpoint = action === 'approved' ? endPoints.approveSparePartUsage : endPoints.rejectSparePartUsage;
        const response = await dispatch(handlePostRequest(payload, endpoint, true));
        if (response?.status === 200) {
            // If approved and we have an active repair order, also link this usage to that order
            if (action === 'approved' && activeRepairOrder?._id) {
                const linkPayload = { usageId };
                await dispatch(handlePostRequest(linkPayload, endPoints.addRepairOrderUsage(activeRepairOrder._id), false));
            }
            await loadSparePartUsage();
            await loadRepairOrders();
        }
    };

    const sparePartActionsTemplate = (row) => {
        if (row.status !== 'pending') {
            return null;
        }
        return (
            <div className="flex gap-2">
                <Button icon="pi pi-check" className="p-button-rounded p-button-success p-button-sm" tooltip={t('Approve')} onClick={() => handleApproveRejectUsage(row, 'approved')} />
                <Button icon="pi pi-times" className="p-button-rounded p-button-danger p-button-sm" tooltip={t('Reject')} onClick={() => handleApproveRejectUsage(row, 'rejected')} />
            </div>
        );
    };

    return (
        <div className="p-4">
            <ConfirmDialog />
            <style>{`.chat-messages{scrollbar-width:none;-ms-overflow-style:none;} .chat-messages::-webkit-scrollbar{display:none;width:0;height:0;} .chat-messages .text-500{color:rgba(229,231,235,0.85)}`}</style>
            <Button
                type="button"
                label={t('Back to List')}
                icon="pi pi-arrow-left"
                className="p-button-text p-button-sm mb-3"
                onClick={() => navigate(returnTo)}
            />
            <div className="grid align-items-stretch mb-4">
                <div className="col-12 lg:col-8 flex">
                    <div className="card flex flex-column w-full" style={{ minHeight: '420px' }}>
                        <div className="flex justify-content-between align-items-center mb-3">
                            <div>
                                <h5 className="m-0">{t('Chat Box')}</h5>
                                {complaint?.customerComplaintCount > 2 && <span className="text-xs text-red-500 font-semibold">{t('Frequent customer')}: {complaint.customerComplaintCount} {t('complaints recorded')}</span>}
                            </div>
                            <span className="text-sm text-500">{t('Ticket')} #{complaint?._id ? complaint._id.substring(complaint._id.length - 6) : '-'}</span>
                        </div>

                        <div
                            className="chat-messages"
                            style={{
                                background: 'transparent',
                                borderRadius: '8px',
                                padding: '1.5rem',
                                height: '320px',
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '1.5rem',
                                color: '#e5e7eb'
                            }}
                            ref={messagesContainerRef}
                        >
                            {combinedMessages.length === 0 && fallbackTimeline.length === 0 && (
                                <span className="text-500 text-sm">{t('No messages yet for this ticket.')}</span>
                            )}
                            {combinedMessages.length === 0 && fallbackTimeline.length > 0 && (
                                <div className="flex flex-column gap-3">
                                    <span className="text-500 text-sm">
                                        {t('No chat history was saved for this older ticket. Showing the available complaint timeline instead.')}
                                    </span>
                                    {fallbackTimeline.map((item) => (
                                        <div
                                            key={item.id}
                                            style={{
                                                background: 'rgba(37,99,235,0.08)',
                                                border: '1px solid rgba(37,99,235,0.12)',
                                                borderRadius: '12px',
                                                padding: '0.85rem 1rem',
                                                color: '#1f2937'
                                            }}
                                        >
                                            <div className="flex justify-content-between align-items-start gap-3">
                                                <div>
                                                    <div style={{ fontWeight: 600, color: '#1e3a8a' }}>{item.title}</div>
                                                    <div className="mt-1" style={{ color: '#334155' }}>{item.description}</div>
                                                    {item.updatedBy ? (
                                                        <small style={{ color: '#64748b' }}>{t('Updated by')} {item.updatedBy}</small>
                                                    ) : null}
                                                </div>
                                                <small style={{ color: '#64748b', whiteSpace: 'nowrap' }}>{item.time || '-'}</small>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {combinedMessages.map((msg) => {
                                const mediaType = getChatMediaType(msg);
                                const mediaUrl = mediaType !== 'text' ? resolveChatImageUrl(msg.text) : null;

                                // Never rendered as a message bubble: the customer did not
                                // receive this. Strip the "[system] " prefix, which only
                                // existed to mark the entry before the flag did.
                                if (msg.internal) {
                                    return (
                                        <div
                                            key={msg.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                gap: '0.5rem',
                                                margin: '0.15rem 0',
                                                padding: '0.3rem 0.6rem',
                                                borderLeft: '2px solid rgba(148,163,184,0.35)',
                                                color: '#94a3b8',
                                                fontSize: '0.8rem',
                                                fontStyle: 'italic',
                                                lineHeight: 1.4
                                            }}
                                        >
                                            <span style={{ fontStyle: 'normal', opacity: 0.75, whiteSpace: 'nowrap' }}>
                                                {t('Internal note')} {msg.time && `• ${msg.time}`}
                                            </span>
                                            <span>{String(msg.text || '').replace(/^\[system\]\s*/i, '')}</span>
                                        </div>
                                    );
                                }

                                return (
                                <div
                                    key={msg.id}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: msg.direction === 'incoming' ? 'flex-start' : 'flex-end'
                                    }}
                                >
                                    <span className="text-500 text-sm mb-1">
                                        {msg.author} {msg.time && `• ${msg.time}`}
                                    </span>
                                    <div
                                        style={{
                                            background: msg.direction === 'system'
                                                ? '#f1f5f9'
                                                : msg.direction === 'incoming'
                                                    ? '#0b1220'
                                                    : '#2563eb',
                                            border: msg.direction === 'system'
                                                ? '1px solid #cbd5e1'
                                                : '1px solid rgba(255,255,255,0.06)',
                                            borderRadius: msg.direction === 'system' ? '12px' : '14px',
                                                padding: getChatMediaType(msg) !== 'text' ? '0.5rem' : msg.direction === 'system' ? '0.6rem 0.85rem' : '0.75rem 1rem',
                                            maxWidth: msg.direction === 'system' ? '92%' : '85%',
                                            color: msg.direction === 'system'
                                                ? '#334155'
                                                : msg.direction === 'incoming'
                                                    ? '#e5e7eb'
                                                    : '#ffffff',
                                            fontSize: '0.9rem',
                                                lineHeight: 1.4,
                                                overflow: 'hidden'
                                            }}
                                        >
                                            {mediaType !== 'text' && mediaUrl ? (
                                                <div>
                                                    {failedMediaUrls.has(mediaUrl) ? (
                                                        <div
                                                            style={{
                                                                padding: '0.45rem 0.6rem',
                                                                borderRadius: '8px',
                                                                background: 'rgba(148,163,184,0.2)',
                                                                color: '#e2e8f0',
                                                                fontSize: '0.85rem'
                                                            }}
                                                        >
                                                            {t('Media unavailable (historical)')}
                                                        </div>
                                                    ) : mediaType === 'video' ? (
                                                        <video
                                                            src={mediaUrl}
                                                            controls
                                                            preload="metadata"
                                                            style={{
                                                                maxWidth: '260px',
                                                                maxHeight: '220px',
                                                                width: '100%',
                                                                display: 'block',
                                                                borderRadius: '8px',
                                                                backgroundColor: '#0f172a'
                                                            }}
                                                            onError={() => {
                                                                setFailedMediaUrls((prev) => {
                                                                    const next = new Set(prev);
                                                                    next.add(mediaUrl);
                                                                    return next;
                                                                });
                                                            }}
                                                        />
                                                    ) : (
                                                        <img
                                                            src={mediaUrl}
                                                            alt={t('Chat image')}
                                                            style={{
                                                                maxWidth: '200px',
                                                                maxHeight: '200px',
                                                                width: 'auto',
                                                                height: 'auto',
                                                                display: 'block',
                                                                borderRadius: '8px',
                                                                cursor: 'pointer',
                                                                objectFit: 'cover'
                                                            }}
                                                            onClick={() => {
                                                                setSelectedImageUrl(mediaUrl);
                                                                setImagePreviewVisible(true);
                                                            }}
                                                            onError={(e) => {
                                                                e.target.alt = t('Image failed to load');
                                                                setFailedMediaUrls((prev) => {
                                                                    const next = new Set(prev);
                                                                    next.add(mediaUrl);
                                                                    return next;
                                                                });
                                                            }}
                                                        />
                                                    )}
                                                    {msg.caption ? (
                                                        <div style={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}>{msg.caption}</div>
                                                    ) : null}
                                                </div>
                                            ) : (
                                                <span>{msg.text}</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {complaint?.status !== 'close' && (
                            <div className="mt-4">
                                {!complaint?.sourceVapiCallId && !aiDraft && !aiDraftLoading && (
                                    <Button
                                        label={t('Suggest a reply')}
                                        icon="pi pi-sparkles"
                                        className="p-button-text p-button-sm"
                                        onClick={requestAiDraft}
                                    />
                                )}
                                {aiDraftLoading && <span className="text-sm text-500">{t('Generating suggestion...')}</span>}
                                {aiDraftSkipped && !aiDraftLoading && (
                                    <span className="text-sm text-500">{t('No suggestion')}: {aiDraftSkipped}</span>
                                )}
                                {aiDraft && !aiDraftIsStale && (
                                    <div className="p-3 border-1 border-dashed border-round surface-100">
                                        <div className="flex align-items-center justify-content-between mb-2">
                                            <span className="text-xs font-semibold text-primary">
                                                {t('AI draft — review before sending')}
                                            </span>
                                            <Button
                                                icon="pi pi-times"
                                                className="p-button-text p-button-sm"
                                                onClick={() => setAiDraft(null)}
                                                tooltip={t('Discard')}
                                            />
                                        </div>
                                        <div className="text-sm mb-2 white-space-pre-wrap">{aiDraft.text}</div>
                                        <Button
                                            label={t('Use draft')}
                                            icon="pi pi-arrow-down"
                                            className="p-button-sm"
                                            onClick={async () => {
                                                await dispatch(handlePostRequest({}, endPoints.recordComplaintDraftUsed(complaint._id), false, false));
                                                setMessage(aiDraft.text);
                                                setAiDraft(null);
                                            }}
                                        />
                                    </div>
                                )}
                                {aiDraft && aiDraftIsStale && (
                                    <span className="text-sm text-500">
                                        {t('The suggestion is out of date — the customer wrote again.')}
                                    </span>
                                )}
                            </div>
                        )}

                        <div className="mt-4">
                            <InputTextarea
                                rows={3}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                className="w-full"
                                placeholder={complaint?.status === 'close' ? t('Complaint is closed. Chat is disabled.') : t('Type a message...')}
                                autoResize
                                disabled={complaint?.status === 'close' || sendingChatReply}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend();
                                    }
                                }}
                            />
                            <div className="mt-2">
                                <PhotoUploader
                                    label={t('Attach photo')}
                                    value={chatPhoto}
                                    onChange={setChatPhoto}
                                    helperText={t('You can add an optional caption in the message box.')}
                                    disabled={complaint?.status === 'close' || sendingChatReply}
                                    maxUploadBytes={1.5 * 1024 * 1024}
                                    maxDimension={1600}
                                    forceJpeg
                                />
                            </div>
                            <div className="flex justify-content-between align-items-center mt-2 gap-2">
                                <div>
                                    {chatPhoto && (
                                        <Button
                                            type="button"
                                            label={t('Remove photo')}
                                            icon="pi pi-times"
                                            className="p-button-text p-button-danger p-button-sm"
                                            onClick={() => setChatPhoto('')}
                                            disabled={sendingChatReply}
                                        />
                                    )}
                                </div>
                                <Button
                                    label={sendingChatReply ? t('Sending...') : t('Send')}
                                    icon={sendingChatReply ? 'pi pi-spin pi-spinner' : 'pi pi-send'}
                                    onClick={handleSend}
                                    disabled={(!message.trim() && !chatPhoto) || complaint?.status === 'close' || sendingChatReply}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12 lg:col-4 flex">
                    <Card className="w-full mb-3" style={{ minHeight: '420px' }}>
                        <h5 className="mb-3">{t('Complaint Details')}</h5>
                        {complaint?._id && canReadCallHistory(currentRoleUid, currentAdminUser) && <SupportNotes ticketId={complaint._id} language={preferredLanguage} />}
                        {complaint?.sourceVapiCallId && canReadCallHistory(currentRoleUid, currentAdminUser) && <Button className="p-button-outlined mb-3" icon="pi pi-phone" label={preferredLanguage === 'es' ? 'Ver llamada, transcripción y audio' : 'View call, transcript and audio'} onClick={() => navigate(`/call-history?call=${encodeURIComponent(complaint.sourceVapiCallId)}`)} />}
                        {complaint ? (
                            <div className="flex flex-column gap-3 text-sm">
                                <div>
                                    <span className="text-500 block">{t('Created At')}</span>
                                    <strong>{formatLocalizedDateTime(complaint?.createdAt, preferredLanguage)}</strong>
                                </div>
                                <div>
                                    <span className="text-500 block">{t('WhatsApp Number')}</span>
                                    <strong>{complaint.whatsAppNumber}</strong>
                                </div>
                                <div>
                                    <span className="text-500 block">{t('Customer Name')}</span>
                                    <div className="flex gap-2 mt-1">
                                        {complaint.customerName ? (
                                            <strong>{complaint.customerName}</strong>
                                        ) : (
                                            <>
                                                <InputText value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder={t('Add customer name')} className="p-inputtext-sm w-full" />
                                                <Button icon="pi pi-save" className="p-button-sm p-button-success" disabled={!customerName.trim() || savingCustomerName} onClick={handleSaveCustomerName} tooltip={t('Save customer name')} />
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <span className="text-500 block">{t('Machine')}</span>
                                    <strong>
                                        {complaint.machine?.serialNumber || complaint.machine?.deviceId || '-'}
                                    </strong>
                                </div>
                                <div>
                                    <span className="text-500 block">{t('Location')}</span>
                                    <strong>{complaint.machine?.location || '-'}</strong>
                                </div>
                                {complaint?.levelOneQuestion?.text && (
                                    <div>
                                        <span className="text-500 block">{t('Level 1')}</span>
                                        <strong>{complaint.levelOneQuestion.text}</strong>
                                    </div>
                                )}
                                {complaint?.levelTwoQuestion?.text && (
                                    <div>
                                        <span className="text-500 block">{t('Level 2')}</span>
                                        <strong>{complaint.levelTwoQuestion.text}</strong>
                                    </div>
                                )}
                                {complaint?.levelThreeQuestion?.text && (
                                    <div>
                                        <span className="text-500 block">{t('Level 3')}</span>
                                        <strong>{complaint.levelThreeQuestion.text}</strong>
                                    </div>
                                )}
                                <div>
                                    <span className="text-500 block">{t('Total Complaints from this Customer')}</span>
                                    <strong>{complaint.customerComplaintCount || 1}</strong>
                                </div>
                                {shouldShowCreateRepairOrderButton && (
                                    <div>
                                        <Button
                                            label={t('Create Repair Order')}
                                            icon="pi pi-plus"
                                            className="p-button-success p-button-sm w-full"
                                            onClick={handleCreateRepairOrderClick}
                                            disabled={creatingRepairOrder || complaint.status === 'close'}
                                            loading={creatingRepairOrder}
                                        />
                                    </div>
                                )}
                                <div className="mt-2 pt-2" style={{ borderTop: '1px solid #e5e7eb' }}>
                                    <span className="text-500 block">{t('Status')}</span>
                                    <div className="flex align-items-center gap-2 mt-1">
                                        <Dropdown value={complaint.status} options={complaintStatusOptions} optionDisabled="disabled" onChange={(e) => handleStatusChange(e.value)} disabled={updatingStatus || complaint.status === 'close' || cashReturnAssigned} className="p-inputtext-sm w-full" />
                                    </div>
                                    {hasTechnicalRoute || hasFinanceRoute ? (
                                        <small className="block mt-2" style={{ color: hasRouteConflict ? '#b91c1c' : '#475569' }}>
                                            {t(hasRouteConflict
                                                ? 'Route conflict: Technicians and Finance'
                                                : hasTechnicalRoute
                                                ? 'Exclusive route: Technicians'
                                                : 'Exclusive route: Finance')}
                                        </small>
                                    ) : null}
                                    {requiresLifetimeRefundReview ? (
                                        <div className="mt-2 p-2 border-round text-sm" style={{ background: '#fff7ed', border: '1px solid #f97316', color: '#9a3412' }}>
                                            <strong className="block mb-1">
                                                {refundRiskReview?.decision === 'sent_to_finance' ? 'Revisión humana realizada' : 'Revisión humana requerida'}
                                            </strong>
                                            <div>
                                                USD {Number(refundRiskReview.lifetimeAmount || 0).toFixed(2)} acumulados en {Number(refundRiskReview.relatedCount || 0)} solicitudes.
                                            </div>
                                            <div>
                                                Coincidencia: {(refundRiskReview.matchedBy || []).map((item) => item === 'bank_account' ? 'cuenta bancaria' : 'WhatsApp').join(', ') || '-'}.
                                                {' '}Rechazadas: {Number(refundRiskReview.rejectedCount || 0)}.
                                            </div>
                                            <div className="mt-1">
                                                {refundRiskReview?.decision === 'sent_to_finance'
                                                    ? `Un usuario autorizado decidió enviarlo a Finanzas${refundRiskReview.reviewedAt ? ` el ${formatChatTimestamp(refundRiskReview.reviewedAt, preferredLanguage)}` : ''}.`
                                                    : 'Customer Support debe revisarlo y decidir si lo envía a Finanzas.'}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                                {['admin', 'superAdmin', 'technician'].includes(currentRoleUid) && complaint?._id && (
                                    <div className="mt-2">
                                        <SupportHandoffButton complaintId={complaint._id} t={t}
                                            onTransferred={() => navigate(currentRoleUid === 'technician' ? '/repair-orders' : '/open-complaints')} />
                                    </div>
                                )}
                                {canSendToTechnicians && !hasFinanceRoute && !isTechnicianTeam && complaint.status !== 'close' && complaint.status !== 'in_progress' && !cashReturnAssigned ? (
                                    <div className="mt-1">
                                        <Button
                                            label="Enviar a Técnicos"
                                            icon="pi pi-send"
                                            className="p-button-warning p-button-sm w-full"
                                            onClick={() => setTechnicalHandoffVisible(true)}
                                        />
                                    </div>
                                ) : null}
                                {cashReturnAssigned ? (
                                    <div className="mt-2 p-2 border-round text-sm" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                                        <strong className="text-blue-700 block">{t('Legacy product delivery in progress')}</strong>
                                        <span className="text-blue-700">
                                            {assignedCashReturnOperator || t('Assigned operator')}
                                            {cashReturn.assignedAt ? ` · ${formatChatTimestamp(cashReturn.assignedAt, preferredLanguage)}` : ''}
                                        </span>
                                    </div>
                                ) : null}
                                {(complaint?.refundEligible || canManageRefundFinanceActions) && !cashReturnAssigned ? (
                                <div className="mt-2 pt-3" style={{ borderTop: '1px solid #e5e7eb' }}>
                                    <div className="flex justify-content-between align-items-center gap-2 mb-2">
                                        <span className="font-semibold">{t('Refund')}</span>
                                    </div>
                                    {!complaint?.refundEligible && canManageRefundFinanceActions ? (
                                        <p className="text-500 text-sm mb-3">
                                            Complete los datos de reembolso acordados con el cliente. Guarde el reembolso y seleccione Finanzas en Estado para enviarlo a revisión.
                                        </p>
                                    ) : null}
                                    {hasUnreviewedAchCorrection ? (
                                        <div className="mb-3 p-2 border-round text-sm" style={{ background: '#fff7ed', border: '1px solid #fb923c', color: '#9a3412' }}>
                                            <strong className="block mb-1">El cliente corrigió datos ACH después de confirmar.</strong>
                                            <span>Revise el chat, actualice este formulario y pulse Guardar reembolso antes de enviar el ticket a Finanzas.</span>
                                        </div>
                                    ) : null}
                                    <div className="grid formgrid">
                                        <div className="col-12 mb-2">
                                            <span className="text-500 block mb-1">{t('Customer Name')}</span>
                                            <InputText value={refundForm.customerName} onChange={(e) => updateRefundForm('customerName', e.target.value)} disabled={refundEditorLocked} className="p-inputtext-sm w-full" />
                                        </div>
                                        <div className="col-12 mb-2">
                                            <span className="text-500 block mb-1">{t('Account Number')}</span>
                                            <InputText value={refundForm.accountNumber} onChange={(e) => updateRefundForm('accountNumber', e.target.value)} disabled={refundEditorLocked} className="p-inputtext-sm w-full" />
                                        </div>
                                        <div className="col-12 md:col-6 mb-2">
                                            <span className="text-500 block mb-1">{t('Account Type')}</span>
                                            <Dropdown value={refundForm.accountType} options={refundAccountTypeOptions} onChange={(e) => updateRefundForm('accountType', e.value)} disabled={refundEditorLocked} placeholder={t('Select account type')} className="p-inputtext-sm w-full" />
                                        </div>
                                        <div className="col-12 md:col-6 mb-2">
                                            <span className="text-500 block mb-1">{t('Bank Entity')}</span>
                                            <Dropdown value={refundForm.bankName} options={refundBankEntityOptions} onChange={(e) => updateRefundForm('bankName', e.value)} disabled={refundEditorLocked} className="p-inputtext-sm w-full" />
                                        </div>
                                        <div className="col-12 md:col-6 mb-2">
                                            <span className="text-500 block mb-1">{t('Amount')}</span>
                                            <InputText type="number" min="0" step="0.01" value={refundForm.amount} onChange={(e) => updateRefundForm('amount', e.target.value)} disabled={refundEditorLocked} className="p-inputtext-sm w-full" />
                                        </div>
                                        <div className="col-12 mb-2">
                                            <span className="text-500 block mb-1">{t('Refund Reason')}</span>
                                            <InputTextarea rows={2} value={refundForm.reason} onChange={(e) => updateRefundForm('reason', e.target.value)} disabled={refundEditorLocked} className="p-inputtext-sm w-full" />
                                        </div>
                                    </div>
                                    {refundComments.length ? (
                                        <div className="mb-2">
                                            <span className="text-500 block mb-1">{t('Refund Comments')}</span>
                                            <div className="flex flex-column gap-2">
                                                {refundComments.map((comment, index) => (
                                                    <div key={comment?._id || `${comment?.createdAt || 'comment'}-${index}`} className="p-2 border-1 surface-border border-round">
                                                        <div className="text-sm text-900" style={{ whiteSpace: 'pre-wrap' }}>{comment?.text || '-'}</div>
                                                        <div className="text-xs text-500 mt-1">
                                                            {formatAdminUserName(comment?.createdBy) || t('Unknown user')}
                                                            {comment?.legacyReason ? ` - ${t('Saved refund reason')}` : ''}
                                                            {comment?.refundAudit ? ` - ${t('Refund history')}` : ''}
                                                            {comment?.createdAt ? ` - ${formatChatTimestamp(comment.createdAt, preferredLanguage)}` : ''}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : null}
                                    {complaint?.refund?.duplicateWarnings?.length ? (
                                        <div className="text-orange-500 text-xs mb-2">
                                            {complaint.refund.duplicateWarnings.join(' | ')}
                                        </div>
                                    ) : null}
                                    {complaint?.refund?.batchId ? (
                                        <div className="text-500 text-xs mb-2">
                                            {t('Batch')}: {complaint.refund.batchId}
                                        </div>
                                    ) : null}
                                    <div className="flex gap-2 flex-wrap">
                                        <Button label={t('Save Refund')} icon="pi pi-save" className="p-button-sm" disabled={savingRefund || refundEditorLocked} onClick={() => saveRefund()} />
                                    </div>
                                </div>
                                ) : null}
                            </div>
                        ) : (
                            <span className="text-500 text-sm">{t('Loading complaint details...')}</span>
                        )}
                    </Card>
                </div>
            </div>

            {activeRepairOrder && (
                <>
                    {/* Repair summary */}
                    <div className="grid mb-4">
                        <div className="col-12 md:col-6">
                            <Card>
                                <h5 className="mb-3">{t('Repair Assignment')}</h5>
                                <>
                                    <div className="mb-2 text-sm">
                                        <span className="text-500 block">{t('Repair Order ID')}</span>
                                        <strong>{activeRepairOrder._id}</strong>
                                    </div>
                                    <div className="mb-2 text-sm">
                                        <span className="text-500 block">{t('Current Technician')}</span>
                                        <strong>{activeRepairOrder.technician ? `${activeRepairOrder.technician.firstName || ''} ${activeRepairOrder.technician.lastName || ''}`.trim() : t('Not assigned')}</strong>
                                    </div>
                                    <div className="mb-3 text-sm">
                                        <span className="text-500 block mb-2">{t('Assign Technician')}</span>
                                        <Dropdown value={selectedTechnician} options={technicianOptions} onChange={(e) => setSelectedTechnician(e.value)} placeholder={t('Select Technician')} className="w-full" showClear />
                                    </div>
                                    <Button label={t('Update Assignment')} icon="pi pi-user-edit" className="p-button-primary p-button-sm w-full" onClick={handleAssignTechnician} disabled={!selectedTechnician} />
                                </>
                            </Card>
                        </div>

                        <div className="col-12 md:col-6">
                            <Card>
                                <h5 className="mb-3">{t('Repair Order Details')}</h5>
                                <div className="flex flex-column gap-2 text-sm">
                                    <div>
                                        <span className="text-500 block">{t('Status')}</span>
                                        <strong style={{ color: activeRepairOrder.status === 'completed' ? '#16a34a' : '#eab308' }}>{activeRepairOrder.status === 'completed' ? t('Completed') : t('Open')}</strong>
                                    </div>
                                    <div>
                                        <span className="text-500 block">{t('Failure Type')}</span>
                                        <strong>{activeRepairOrder.typeOfFailure || '-'}</strong>
                                    </div>
                                    <div>
                                        <span className="text-500 block">{t('Failure Category')}</span>
                                        <strong>{activeRepairOrder.failureCategory || '-'}</strong>
                                    </div>
                                    <div>
                                        <span className="text-500 block">{t('Final Status')}</span>
                                        <strong>{activeRepairOrder.finalStatus || '-'}</strong>
                                    </div>
                                    <div>
                                        <span className="text-500 block">{t('Time Consumed')}</span>
                                        <strong>{formatTimeFromMinutes(activeRepairOrder.timeConsumedMinutes, preferredLanguage)}</strong>
                                    </div>
                                    <div>
                                        <span className="text-500 block">{t('Closed At')}</span>
                                        <strong>{formatLocalizedDateTime(activeRepairOrder.closedAt, preferredLanguage)}</strong>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div>

                    <div className="grid mb-4">
                        <div className="col-12">
                            <Card>
                                <div className="flex justify-content-between align-items-center mb-3">
                                    <h5 className="m-0">{t('Spare Part Requests for this Ticket')}</h5>
                                </div>
                                <DataTable value={sparePartUsage} emptyMessage={t('No spare part requests linked to this complaint yet.')} className="p-datatable-sm" rows={5} paginator rowsPerPageOptions={[5, 10, 20]}>
                                    <Column header={t('Part')} body={(row) => `${row.sparePart?.sku || ''} – ${row.sparePart?.description || ''}`.trim()} />
                                    <Column field="quantity" header={t('Qty')} />
                                    <Column header={t('Technician')} body={(row) => `${row.technician?.firstName || ''} ${row.technician?.lastName || ''}`.trim()} />
                                    <Column header={t('Approved By')} body={(row) => `${row.supervisor?.firstName || ''} ${row.supervisor?.lastName || ''}`.trim() || '-'} />
                                    <Column header={t('Reviewed At')} body={(row) => formatLocalizedDateTime(row.authorizationDate, preferredLanguage)} />
                                    <Column header={t('Review Comment')} body={(row) => row.reviewComment || '-'} />
                                    <Column field="status" header={t('Status')} body={sparePartStatusTemplate} />
                                    <Column header={t('Actions')} body={sparePartActionsTemplate} />
                                </DataTable>
                            </Card>
                        </div>
                    </div>
                </>
            )}

            <div className="card">
                <div className="flex justify-content-between align-items-center mb-3">
                    <h5 className="m-0">{t('Logs')}</h5>
                </div>
                <DataTable value={logs} className="p-datatable-sm" rows={5} paginator rowsPerPageOptions={[5, 10, 20]}>
                    <Column header={t('Time')} body={(row) => formatLocalizedDateTime(row.createdAt, preferredLanguage)} />
                    <Column
                        header={t('Status')}
                        body={(row) => {
                            // If team field exists, show "Assign to [team name]"
                            if (row.team) {
                                return `${t('Assign to')} ${row.team}`;
                            }
                            // Otherwise show the status
                            const m = {
                                open: 'Open',
                                in_progress: 'Finance',
                                awaiting_customer: 'Awaiting Customer',
                                close: 'Closed',
                                forwarded: 'Forwarded',
                                pending: 'Pending',
                                system_close: 'System close'
                            };
                            return t(m[row.status] || row.status || '-');
                        }}
                    />
                    <Column field="role" header={t('Role')} />
                    <Column 
                        header={t('Updated By')} 
                        body={(row) => {
                            if (row.user && row.user.trim() !== '' && row.user !== '-') {
                                return row.user.trim();
                            }
                            return '-';
                        }}
                    />
                </DataTable>
            </div>

            <Dialog
                header="Enviar reclamo a Técnicos"
                visible={technicalHandoffVisible}
                style={{ width: '34rem', maxWidth: '95vw' }}
                onHide={() => {
                    if (savingTechnicalHandoff) return;
                    setTechnicalHandoffVisible(false);
                    setTechnicalHandoffReason('');
                }}
                closable={!savingTechnicalHandoff}
                modal
            >
                <p className="mt-0 text-color-secondary">
                    El reclamo saldrá de la bandeja de soporte y quedará disponible para que un técnico lo tome.
                </p>
                <label className="block mb-2 font-semibold">Motivo e instrucciones *</label>
                <InputTextarea
                    value={technicalHandoffReason}
                    onChange={(e) => setTechnicalHandoffReason(e.target.value)}
                    rows={4}
                    maxLength={1000}
                    className="w-full mb-3"
                    autoFocus
                />
                <div className="flex justify-content-end gap-2">
                    <Button
                        label="Cancelar"
                        className="p-button-text"
                        disabled={savingTechnicalHandoff}
                        onClick={() => {
                            setTechnicalHandoffVisible(false);
                            setTechnicalHandoffReason('');
                        }}
                    />
                    <Button
                        label="Enviar a Técnicos"
                        icon="pi pi-send"
                        loading={savingTechnicalHandoff}
                        disabled={
                            savingTechnicalHandoff ||
                            technicalHandoffReason.trim().length < 3
                        }
                        onClick={submitTechnicalHandoff}
                    />
                </div>
            </Dialog>

            <Dialog
                header={t('Image Preview')}
                visible={imagePreviewVisible}
                style={{ width: 'auto', maxWidth: '90vw' }}
                contentStyle={{ 
                    maxHeight: '85vh', 
                    overflow: 'hidden',
                    padding: '1rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}
                modal
                onHide={() => setImagePreviewVisible(false)}
            >
                {selectedImageUrl && (
                    <img 
                        src={selectedImageUrl} 
                        alt={t('Preview')} 
                        style={{ 
                            maxWidth: '90vw',
                            maxHeight: '85vh',
                            width: 'auto',
                            height: 'auto', 
                            display: 'block',
                            borderRadius: '8px',
                            objectFit: 'contain'
                        }} 
                    />
                )}
            </Dialog>
        </div>
    );
};

export default ChatBox;
